import type { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CrudLink } from "@/shared/components";
import { PagePaths } from "@/admin/shared/constants";
import type {
  ContractDetailsData,
  ContractDetailsPaymentRow,
} from "@/admin/entities/contracts";
import {
  contractPeriodAmount,
  formatContractCurrency,
  formatContractPeriod,
  formatDay,
  formatMoney,
} from "./billingFormat";
import {
  BILLING_PERIOD_LABEL,
  CONTRACT_ITEM_TYPE_LABEL,
  CONTRACT_PAYMENT_PERIOD_TYPE_LABEL,
  renderContractStatus,
} from "./billingLabels";
import { ContractPaymentStatusLink } from "./ContractPaymentCardLinks";
import { buildContractSummary } from "./contractSummary";
import { GRID_SX } from "./infoGrid";
import { InfoItem } from "./InfoItem";

interface ContractDetailsProps {
  /** CrudShow разворачивает ответ ContractDetailsQuery в пропсы. */
  contract?: ContractDetailsData | null;
  contractPayments?: {
    nodes: ContractDetailsPaymentRow[];
    count: number;
  } | null;
  id?: string;
}

/** Три суммы по контракту в ряд на десктопе. */
const MONEY_GRID_SX = {
  ...GRID_SX,
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
} as const;

/**
 * Вкладка «Детали» контракта (спека 2026-10-16 §5): организация, статус,
 * срок, валюты, строки услуг, ставка в месяц, следующий счёт, оплачено и
 * ждёт оплаты, последние платежи со ссылкой в карточку платежа.
 */
export const ContractDetails: FC<ContractDetailsProps> = ({
  contract,
  contractPayments,
}) => {
  if (!contract) {
    return (
      <Stack sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Контракт не найден
        </Typography>
      </Stack>
    );
  }

  const payments = contractPayments?.nodes ?? [];
  const total = contractPayments?.count ?? payments.length;
  const summary = buildContractSummary(contract, payments);
  const periodLabel =
    BILLING_PERIOD_LABEL[contract.billingPeriodMonths] ??
    `${contract.billingPeriodMonths} мес.`;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "center", mb: 2, flexWrap: "wrap", rowGap: 1 }}
          >
            <Typography variant="h5" component="h2">
              {contract.title}
            </Typography>
            {renderContractStatus(contract.status)}
          </Stack>
          <Box sx={GRID_SX}>
            <InfoItem label="Организация">
              <CrudLink
                path={PagePaths.organizations}
                tab="details"
                id={contract.organizationId}
              >
                {contract.organization?.title ?? contract.organizationId}
              </CrudLink>
            </InfoItem>
            <InfoItem label="Срок">
              {formatContractPeriod(contract.dateFrom, contract.dateTo)}
            </InfoItem>
            <InfoItem label="Валюта контракта и счёта">
              {formatContractCurrency(
                contract.currency,
                contract.invoiceCurrency
              )}
            </InfoItem>
            <InfoItem label="Биллинг-период">{periodLabel}</InfoItem>
            <InfoItem label="Когда выставляем инвойс">
              {CONTRACT_PAYMENT_PERIOD_TYPE_LABEL[contract.paymentPeriodType] ??
                contract.paymentPeriodType}
            </InfoItem>
            <InfoItem label="Срок оплаты, дней">
              {contract.paidPeriodDays}
            </InfoItem>
            <InfoItem label="Ставка в месяц">
              {summary.monthlyLicense
                ? formatMoney(summary.monthlyLicense)
                : "Нет лицензии"}
            </InfoItem>
            <InfoItem label="Сумма за биллинг-период">
              {contractPeriodAmount(contract) || "Нет"}
            </InfoItem>
            {contract.canceledAt && (
              <InfoItem label="Отменён">
                {formatDay(contract.canceledAt)}
              </InfoItem>
            )}
            {contract.note && (
              <InfoItem label="Заметка">{contract.note}</InfoItem>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Строки услуг" />
        <CardContent sx={{ pt: 0 }}>
          <List dense disablePadding>
            {contract.items.map((item, index) => (
              <ListItem
                key={`${item.type}-${index}`}
                disableGutters
                secondaryAction={
                  <Typography variant="body2">
                    {item.amount
                      ? formatMoney(item.amount)
                      : "процент из настроек организации"}
                  </Typography>
                }
              >
                <ListItemText
                  primary={item.title}
                  secondary={CONTRACT_ITEM_TYPE_LABEL[item.type] ?? item.type}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Деньги по контракту" />
        <CardContent sx={{ pt: 0 }}>
          <Box sx={MONEY_GRID_SX}>
            <InfoItem label="Оплачено">{formatMoney(summary.paid)}</InfoItem>
            <InfoItem label="Ждём оплаты">
              <Stack spacing={0.5}>
                <span>{formatMoney(summary.awaiting)}</span>
                {summary.overdueCount > 0 && (
                  <Typography variant="caption" color="error.main">
                    {`просрочено: ${summary.overdueCount}`}
                  </Typography>
                )}
              </Stack>
            </InfoItem>
            <InfoItem label="Следующий счёт">
              {summary.nextInvoice
                ? `${formatDay(summary.nextInvoice.invoiceDate)}, ${formatMoney(summary.nextInvoice.amount)}`
                : "Не запланирован"}
            </InfoItem>
          </Box>
          {total > payments.length && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {`Учтены первые ${payments.length} из ${total} платежей`}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Последние платежи" />
        <CardContent sx={{ pt: 0 }}>
          {summary.recentPayments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Платежей пока нет
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата инвойса</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Счёт</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.recentPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDay(payment.invoiceDate)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(payment.amount)}
                    </TableCell>
                    <TableCell>
                      <ContractPaymentStatusLink
                        payment={payment}
                        cardPath={PagePaths.billingPayments}
                      />
                    </TableCell>
                    <TableCell>{payment.invoice?.number ?? "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};
